<template>
  <div class="filter-card appointment-filter-card">
    <div class="filter-card-header">
      <div class="filter-card-title">
        <i class="fa fa-calendar filter-icon"></i>
        <span>Appointment Filter</span>
      </div>
      <b-dropdown variant="link" size="sm" no-caret right class="filter-menu">
        <template #button-content>
          <i class="fa fa-ellipsis-v text-muted"></i>
        </template>
        <b-dropdown-item @click="$emit('duplicate')">
          <i class="fa fa-copy mr-2"></i>Duplicate
        </b-dropdown-item>
        <b-dropdown-item variant="danger" @click="$emit('remove')">
          <i class="fa fa-trash mr-2"></i>Remove
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="filter-card-body">
      <!-- Mode -->
      <div class="mb-3">
        <div class="filter-radio-group">
          <label class="filter-radio-option">
            <input type="radio" value="none" v-model="localFilter.mode" @change="emitUpdate" />
            <span>With no appointment</span>
          </label>
          <label class="filter-radio-option">
            <input type="radio" value="has" v-model="localFilter.mode" @change="emitUpdate" />
            <span>With an appointment</span>
          </label>
        </div>
      </div>

      <!-- Timeframe + Status Row -->
      <b-row>
        <b-col md="6">
          <div class="mb-3">
            <label class="filter-label">Timeframe <span class="required">*</span></label>
            <div class="timeframe-row">
              <select
                v-model="localFilter.timeframe.direction"
                class="filter-form-input timeframe-direction"
                @change="emitUpdate"
              >
                <option value="last">In the last</option>
                <option value="next">In the next</option>
              </select>
              <input
                v-model.number="localFilter.timeframe.value"
                type="number"
                min="1"
                class="filter-form-input timeframe-number"
                @input="emitUpdate"
              />
              <select
                v-model="localFilter.timeframe.unit"
                class="filter-form-input timeframe-unit"
                @change="emitUpdate"
              >
                <option value="months">Months</option>
                <option value="days">Days</option>
                <option value="years">Years</option>
              </select>
            </div>
          </div>
        </b-col>
        <b-col md="6">
          <div class="mb-3">
            <label class="filter-label">Status <span class="required">*</span></label>
            <multiselect
              v-model="selectedStatuses"
              :options="statusOptions"
              :multiple="true"
              :searchable="false"
              :close-on-select="false"
              :show-labels="false"
              label="text"
              track-by="value"
              placeholder="Select status..."
              class="filter-multiselect"
              @input="onStatusesChange"
            />
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: 'AppointmentFilterCard',
  components: { Multiselect },
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localFilter: JSON.parse(JSON.stringify(this.value)),
      selectedStatuses: [],
      statusOptions: [
        { value: 1, text: 'Scheduled' },
        { value: 2, text: 'Completed' },
        { value: 3, text: 'Unscheduled' },
        { value: 5, text: 'Broken' }
      ]
    };
  },
  watch: {
    value: {
      handler(newVal) {
        this.localFilter = JSON.parse(JSON.stringify(newVal));
        this.syncSelections();
      },
      deep: true
    }
  },
  created() {
    this.syncSelections();
  },
  methods: {
    syncSelections() {
      this.selectedStatuses = (this.localFilter.statuses || [])
        .map(s => this.statusOptions.find(o => o.value === s))
        .filter(Boolean);
    },
    onStatusesChange(selected) {
      this.localFilter.statuses = selected.map(s => s.value);
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('input', { ...this.localFilter });
    }
  }
};
</script>

<style scoped lang="scss">
.appointment-filter-card {
  border-left: 3px solid #f59e0b;
}

.filter-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: visible;
}

.filter-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0.875rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.filter-card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #334155;

  .filter-icon {
    color: #f59e0b;
    font-size: 0.875rem;
  }
}

.filter-menu ::v-deep .btn-link {
  padding: 0.125rem 0.375rem;
  color: #94a3b8;
  &:hover { color: #475569; }
}

.filter-card-body {
  padding: 0.875rem;
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 0.375rem;
  display: block;

  .required {
    color: #ef4444;
  }
}

.filter-multiselect ::v-deep {
  .multiselect__tags {
    min-height: 34px;
    padding: 4px 40px 0 8px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.8125rem;
  }
  .multiselect__tag {
    background: #fef3c7;
    color: #92400e;
    border-radius: 4px;
    font-size: 0.75rem;
    padding: 2px 22px 2px 8px;
    margin-bottom: 2px;
  }
  .multiselect__tag-icon {
    &:after { color: #b45309; }
    &:hover { background: #fde68a; }
  }
  .multiselect__option--highlight {
    background: #f59e0b;
  }
  .multiselect__content-wrapper {
    z-index: 1060;
    border-radius: 6px;
    border-color: #e2e8f0;
  }
  .multiselect__placeholder {
    font-size: 0.8125rem;
    color: #94a3b8;
    padding-top: 0;
    margin-bottom: 0;
  }
}

// Native form input styling (matches Online Scheduling pattern)
.filter-form-input {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.8125rem;
  outline: none;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    border-color: #f59e0b;
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.15);
  }
}

select.filter-form-input {
  height: 34px;
  line-height: 1.2;
  background: white;
  cursor: pointer;
}

.filter-radio-group {
  display: flex;
  gap: 16px;
}

.filter-radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 0;

  input[type="radio"] {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
    accent-color: #f59e0b;
  }
}

.timeframe-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.timeframe-direction {
  width: 120px;
}

.timeframe-number {
  width: 60px;
}

.timeframe-unit {
  width: 100px;
}
</style>
